<template>
  <main class="py-10">
    <UContainer>
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Kundenkonfiguration
          </h1>
          <p class="text-gray-500 dark:text-gray-400 mt-1">
            Verwalten Sie Ihre Kunden und deren Einstellungen.
          </p>
        </div>
        <div class="flex flex-wrap gap-2 sm:w-auto w-full">
          <UButton
            v-if="showBillingButton"
            color="neutral"
            icon="i-heroicons-banknotes"
            label="Abrechnen"
            class="sm:w-auto w-full justify-center"
            @click="isBillingModalOpen = true"
          />
          <UButton
            color="primary"
            icon="i-heroicons-plus"
            label="Neuer Kunde"
            class="sm:w-auto w-full justify-center"
            @click="openCreateModal"
          />
        </div>
      </div>

      <UCard :ui="{ body: 'p-0 sm:p-0' }" class="overflow-x-auto">
        <UTable
          :columns="columns"
          :data="customers"
          :loading="loading"
          class="w-full min-w-[600px] sm:min-w-0"
        >
          <template #loginMode-cell="{ row }">
            <UBadge
              :color="row.original.loginMode === 'local' ? 'primary' : 'neutral'"
              variant="subtle"
              data-test="customer-login-mode"
            >
              {{ row.original.loginMode === 'local' ? 'Lokale Benutzer' : 'Eigener OIDC-Provider' }}
            </UBadge>
            <UBadge
              v-if="row.original.loginMode === 'local' && row.original.localAdminMissing"
              class="ml-2"
              color="warning"
              variant="subtle"
              data-test="customer-admin-missing"
            >
              Kein Administrator
            </UBadge>
          </template>
          <template #microtechCertificate-cell="{ row }">
            <UTooltip
              v-if="certificateSummaries[row.original.customerNo]"
              :ui="{ content: 'h-auto flex-col items-start' }"
            >
              <UBadge
                :color="certificateSummaries[row.original.customerNo]!.color"
                :label="certificateSummaries[row.original.customerNo]!.label"
                data-test="microtech-certificate-badge"
                :icon="
                  certificateSummaries[row.original.customerNo]!.degraded
                    ? 'i-heroicons-exclamation-triangle'
                    : 'i-heroicons-lock-closed'
                "
                variant="subtle"
              />
              <template #content>
                <span
                  v-for="line in certificateSummaries[row.original.customerNo]!.details"
                  :key="line"
                >
                  {{ line }}
                </span>
              </template>
            </UTooltip>
            <span v-else class="text-gray-400">–</span>
          </template>
          <template #microtechConnection-cell="{ row }">
            <UTooltip
              v-if="connectionSummaries[row.original.customerNo]"
              :ui="{ content: 'h-auto flex-col items-start' }"
            >
              <UBadge
                :color="connectionSummaries[row.original.customerNo]!.color"
                :label="connectionSummaries[row.original.customerNo]!.label"
                data-test="microtech-connection-badge"
                :icon="
                  connectionSummaries[row.original.customerNo]!.color === 'error'
                    ? 'i-heroicons-exclamation-triangle'
                    : 'i-heroicons-signal'
                "
                variant="subtle"
              />
              <template #content>
                <span
                  v-for="line in connectionSummaries[row.original.customerNo]!.details"
                  :key="line"
                >
                  {{ line }}
                </span>
              </template>
            </UTooltip>
            <span v-else class="text-gray-400">–</span>
          </template>
          <template #actions-cell="{ row }">
            <div class="flex gap-2 justify-end px-4">
              <UButton
                :to="`/customers/${row.original.customerNo}/tenants`"
                color="neutral"
                icon="i-heroicons-users"
                title="Mandanten verwalten"
                variant="ghost"
              />
              <UButton
                :to="`/customers/${row.original.customerNo}/administration`"
                color="neutral"
                icon="i-heroicons-user-group"
                title="Kunden-Administration"
                variant="ghost"
              />
              <UButton
                color="neutral"
                icon="i-heroicons-sparkles"
                title="Provisionierung anfordern"
                variant="ghost"
                @click="openProvisioningModal(row.original)"
              />
              <UButton
                color="neutral"
                icon="i-heroicons-pencil-square"
                variant="ghost"
                @click="openEditModal(row.original)"
              />
              <UButton
                color="danger"
                icon="i-heroicons-trash"
                variant="ghost"
                @click="confirmDelete(row.original)"
              />
            </div>
          </template>
        </UTable>
      </UCard>
    </UContainer>
  </main>

  <!-- Create/Edit Modal -->
  <UModal
    v-model:open="isModalOpen"
    :title="state.id ? 'Kunde bearbeiten' : 'Neuen Kunden anlegen'"
    description="Geben Sie hier die Details des Kunden ein."
  >
    <template #content>
      <div class="p-6">
        <form class="space-y-4" @submit.prevent="saveCustomer">
          <UFormField label="Kundenname" required>
            <UInput v-model="state.customerName" class="w-full" placeholder="MBCOM GmbH" />
          </UFormField>

          <UFormField label="Kundennummer" required>
            <UInput
              v-model="state.customerNo"
              :disabled="!!state.id"
              class="w-full"
              placeholder="10001"
            />
          </UFormField>

          <UFormField label="Kurzname" required>
            <UInput v-model="state.customerShortName" class="w-full" placeholder="mbcom" />
          </UFormField>

          <UFormField label="Brevo E-Mail">
            <div class="flex gap-2 w-full">
              <UInput
                v-model="state.brevoEmail"
                class="flex-1"
                placeholder="newsletter@mbcom.de"
                type="email"
              />
              <UButton
                v-if="state.brevoEmail"
                :loading="sendingTest"
                color="neutral"
                icon="i-heroicons-envelope"
                label="Testen"
                variant="outline"
                @click="sendTestEmail"
              />
            </div>
          </UFormField>

          <UFormField>
            <UCheckbox v-model="state.brevoActive" label="Brevo Aktiv" />
          </UFormField>

          <UFormField v-if="showLoginModeChoice" label="Anmeldung" data-test="customer-login-mode-choice">
            <URadioGroup v-model="state.loginMode" :items="loginModeOptions" />
          </UFormField>

          <div
            v-if="!state.id && !isLocalCustomer"
            class="border-t border-gray-200 dark:border-gray-800 pt-4"
            data-test="customer-onboarding-section"
          >
            <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Onboarding-Einladung
            </h3>
            <div class="space-y-4">
              <UFormField>
                <UCheckbox
                  v-model="sendOnboardingInvitation"
                  label="Einladung zum Self-Service-Onboarding per E-Mail senden"
                />
              </UFormField>

              <UFormField
                v-if="sendOnboardingInvitation"
                help="Der Kunde erhält einen einmalig verwendbaren Einladungslink (72 Stunden gültig) und konfiguriert Anmeldung, Mandanten und Berechtigungen selbst."
                label="E-Mail-Adresse des Kunden-Administrators"
                required
              >
                <UInput
                  v-model="onboardingAdminEmail"
                  class="w-full"
                  placeholder="it-admin@kunde.de"
                  type="email"
                />
              </UFormField>
            </div>
          </div>

          <div
            v-if="!isLocalCustomer"
            class="border-t border-gray-200 dark:border-gray-800 pt-4"
            data-test="customer-oidc-section"
          >
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                OIDC Konfiguration (Upstream)
              </h3>
              <UButton
                v-if="state.upstreamOIDC!.issuer"
                :loading="testingOIDC"
                color="neutral"
                icon="i-heroicons-beaker"
                label="Testen"
                size="xs"
                variant="outline"
                @click="testOIDC"
              />
            </div>
            <div class="space-y-4">
              <UFormField label="OIDC Issuer">
                <UInput
                  v-model="state.upstreamOIDC!.issuer"
                  class="w-full"
                  placeholder="https://login.microsoftonline.com/..."
                />
              </UFormField>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <UFormField label="Client ID">
                  <UInput
                    v-model="state.upstreamOIDC!.clientId"
                    class="w-full"
                    placeholder="client-id"
                  />
                </UFormField>
                <UFormField label="Client Secret">
                  <UInput
                    v-model="state.upstreamOIDC!.clientSecret"
                    class="w-full"
                    placeholder="••••••••"
                    type="password"
                  />
                  <template #description
                    >Leer lassen, um das bestehende Secret unverändert zu behalten.</template
                  >
                </UFormField>
              </div>
            </div>
          </div>

          <!-- Recovery channel of an existing customer. These addresses are the
               security channel and receive the recovery and restore links; the
               Brevo address above is a broad distribution list and never gets an
               action link. -->
          <div
            v-if="state.id && !isLocalCustomer"
            class="border-t border-gray-200 dark:border-gray-800 pt-4"
            data-test="customer-recovery-section"
          >
            <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Wiederherstellung
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Wiederherstellungs-Adressen sind der Sicherheitskanal des Kunden und erhalten die
              Links zur Wiederherstellung des Zugangs sowie zum Zurücksetzen einer Änderung. Die
              Brevo-Adresse ist ein breiter Verteiler und erhält niemals einen solchen Link.
            </p>

            <p
              v-if="recoveryContacts.length === 0"
              class="text-xs text-warning-600 dark:text-warning-400"
              data-test="customer-recovery-contacts-empty"
            >
              Keine Wiederherstellungs-Adresse hinterlegt. Der Kunde wird beim nächsten
              Konfigurations-Assistenten dazu aufgefordert.
            </p>
            <ul v-else class="space-y-1" data-test="customer-recovery-contacts">
              <li
                v-for="contact in recoveryContacts"
                :key="contact.email"
                class="flex items-center justify-between gap-2 text-sm"
              >
                <span class="text-gray-900 dark:text-white">{{ contact.email }}</span>
                <UBadge
                  :color="contact.verified ? 'success' : 'warning'"
                  :label="contact.verified ? 'Bestätigt' : 'Bestätigung offen'"
                  variant="subtle"
                />
              </li>
            </ul>

            <p class="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Bestätigte Adressen: {{ verifiedRecoveryContacts.length }} von
              {{ recoveryContacts.length }}. Die Anzahl der verbleibenden
              Wiederherstellungscodes sieht der Kunde im Konfigurations-Assistenten.
            </p>

            <div class="mt-3">
              <UButton
                :loading="regeneratingRecoveryCodes"
                color="neutral"
                icon="i-heroicons-key"
                label="Wiederherstellungscodes neu erstellen"
                size="xs"
                variant="outline"
                data-test="customer-recovery-codes-regenerate"
                @click="regenerateRecoveryCodesForCustomer"
              />
            </div>

            <div
              v-if="regeneratedRecoveryCodes?.codes?.length"
              class="mt-3 p-3 rounded-lg bg-warning-50 dark:bg-warning-950/40 border border-warning-200 dark:border-warning-800/50 space-y-2"
              data-test="customer-recovery-codes-panel"
            >
              <p class="text-xs text-gray-700 dark:text-gray-300">
                Diese Codes werden nur jetzt angezeigt. Die bisherigen Codes sind ungültig.
                Verbleibende Codes: {{ regeneratedRecoveryCodes.remaining }}.
              </p>
              <ul class="grid grid-cols-1 sm:grid-cols-2 gap-1 font-mono text-xs text-gray-900 dark:text-white">
                <li v-for="code in regeneratedRecoveryCodes.codes" :key="code">
                  {{ code }}
                </li>
              </ul>
              <UButton
                color="neutral"
                icon="i-heroicons-arrow-down-tray"
                label="Codes als Textdatei herunterladen"
                size="xs"
                variant="outline"
                data-test="customer-recovery-codes-download"
                @click="downloadRegeneratedRecoveryCodes"
              />
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <UButton
              color="neutral"
              label="Abbrechen"
              variant="ghost"
              @click="isModalOpen = false"
            />
            <UButton :loading="saving" color="primary" label="Speichern" type="submit" />
          </div>
        </form>
      </div>
    </template>
  </UModal>

  <!-- Provisioning Invitation Modal -->
  <UModal
    v-model:open="isProvisioningModalOpen"
    title="Provisionierung anfordern"
    description="Öffnen Sie den Konfigurationsassistenten in dieser Sitzung oder senden Sie dem Kunden einen Einladungslink."
  >
    <template #content>
      <div class="p-6">
        <form class="space-y-4" @submit.prevent="sendProvisioningInvitation">
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Kunde:
            <strong class="text-gray-900 dark:text-white">
              {{ provisioningCustomer?.customerName }}
            </strong>
            ({{ provisioningCustomer?.customerNo }})
          </p>

          <UFormField :help="selectedProvisioningDescription" label="Prozessart" required>
            <USelect
              v-model="provisioningState.type"
              :items="provisioningTypeOptions"
              class="w-full"
            />
          </UFormField>

          <UFormField v-if="canOpenInSession" label="Übergabe" required>
            <USelect
              v-model="provisioningState.delivery"
              :items="provisioningDeliveryOptions"
              class="w-full"
            />
          </UFormField>

          <UFormField
            v-if="!usesSessionDelivery"
            help="Der Einladungslink wird über Brevo versendet, ist einmalig verwendbar und läuft nach 72 Stunden ab."
            label="E-Mail-Adresse für die Einladung"
            required
          >
            <UInput
              v-model="provisioningState.email"
              class="w-full"
              placeholder="it-admin@kunde.de"
              required
              type="email"
            />
          </UFormField>

          <UAlert
            v-if="usesSessionDelivery"
            color="neutral"
            icon="i-heroicons-information-circle"
            title="Assistent wird hier geöffnet"
            description="Der Assistent für die gesamte Konfiguration wird direkt in dieser Sitzung geöffnet. Es wird keine E-Mail versendet."
            variant="subtle"
          />
          <UAlert
            v-else
            color="neutral"
            icon="i-heroicons-information-circle"
            title="Der Kunde führt den Assistenten selbst aus"
            description="Der Prozess wird angelegt und per E-Mail übergeben. Es wird keine Sitzung in Ihrem Browser geöffnet."
            variant="subtle"
          />

          <div class="flex justify-end gap-3 pt-2">
            <UButton
              color="neutral"
              label="Abbrechen"
              variant="ghost"
              @click="isProvisioningModalOpen = false"
            />
            <UButton
              :icon="usesSessionDelivery ? 'i-heroicons-sparkles' : 'i-heroicons-paper-airplane'"
              :label="usesSessionDelivery ? 'Konfiguration bearbeiten' : 'Einladung senden'"
              :loading="sendingProvisioning"
              color="primary"
              type="submit"
            />
          </div>
        </form>
      </div>
    </template>
  </UModal>

  <!-- Delete Confirmation Modal -->
  <UModal
    v-model:open="isDeleteModalOpen"
    description="Sind Sie sicher, dass Sie diesen Kunden löschen möchten?"
    title="Kunde löschen"
  >
    <template #content>
      <div class="p-6 space-y-4">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Möchten Sie den Kunden <strong>{{ customerToDelete?.customerName }}</strong> wirklich
          löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="flex justify-end gap-3 pt-2">
          <UButton
            color="neutral"
            label="Abbrechen"
            variant="ghost"
            @click="isDeleteModalOpen = false"
          />
          <UButton :loading="deleting" color="danger" label="Löschen" @click="deleteCustomer" />
        </div>
      </div>
    </template>
  </UModal>

  <!-- Billing Confirmation Modal -->
  <UModal
    v-model:open="isBillingModalOpen"
    description="Möchten Sie die monatliche Abrechnung für den Vormonat jetzt starten?"
    title="Abrechnung durchführen"
  >
    <template #content>
      <div class="p-6 space-y-4">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Hierdurch wird die automatisierte Abrechnung aller abrechenbaren Kunden für den vergangenen Monat angestoßen und Rechnungen im Microtech ERP erstellt.
          Mit dem Testlauf sehen Sie vorher, welche Rechnungen erstellt würden.
        </p>
        <div class="flex justify-end gap-3 pt-2">
          <UButton
            color="neutral"
            label="Abbrechen"
            variant="ghost"
            @click="isBillingModalOpen = false"
          />
          <UButton
            color="neutral"
            icon="i-heroicons-beaker"
            label="Testlauf (Dry Run)"
            variant="outline"
            @click="openBillingDryRun"
          />
          <UButton
            :loading="triggeringBilling"
            color="primary"
            label="Abrechnung starten"
            @click="triggerBilling"
          />
        </div>
      </div>
    </template>
  </UModal>

  <BillingDryRunModal v-model:open="isBillingDryRunOpen" />
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref, computed } from 'vue'

import axios from 'axios'
import type { Customer } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { isPortalAdmin } from '@/utils/roles'
import BillingDryRunModal from '@/components/BillingDryRunModal.vue'
import {
  summarizeCertificateExpiry,
  type CertificateExpirySummary,
  type CustomerCertificateExpiry,
} from '@/utils/certificate-expiry'
import { summarizeServerConnection, type ServerConnectionSummary } from '@/utils/server-connection'
import {
  useOnboarding,
  type OnboardingType,
  type ProvisioningDelivery,
  type RecoveryCodesResult,
} from '@/composables/useOnboarding'

const customers = ref<Customer[]>([])
const toast = useToast()

const authStore = useAuthStore()

// The four former change types were merged into one self-service process, so
// only the three process types the engine knows are offered here.
const provisioningTypeOptions = [
  { label: 'Erstmaliges Onboarding', value: 'INITIAL' as OnboardingType },
  { label: 'Konfiguration bearbeiten', value: 'SELF_SERVICE' as OnboardingType },
  { label: 'Anmeldung wiederherstellen', value: 'OIDC_RECOVERY' as OnboardingType },
]

const provisioningDescriptions: Record<OnboardingType, string> = {
  INITIAL:
    'Erstmalige Einrichtung: Der Kunde konfiguriert Anmeldung, Mandanten und Berechtigungen vollständig selbst.',
  SELF_SERVICE:
    'Ein Assistent für die gesamte Konfiguration: Kontakt, Anmeldung, Mandanten und Berechtigungen. Der Kunde kann ihn auch selbst im Portal starten.',
  OIDC_RECOVERY:
    'Notfallprozess, wenn sich beim Kunden niemand mehr anmelden kann. Erfordert Portal-Administrator-Rechte.',
}

// A self-service change can either be opened directly in the current Portal
// session or handed over to the customer by email. The initial onboarding and
// the recovery are always delivered by email.
const provisioningDeliveryOptions = [
  { label: 'Konfiguration bearbeiten (in dieser Sitzung)', value: 'SESSION' as ProvisioningDelivery },
  { label: 'Einladung senden (per E-Mail)', value: 'EMAIL' as ProvisioningDelivery },
]

const isProvisioningModalOpen = ref(false)
const sendingProvisioning = ref(false)
const provisioningCustomer = ref<Customer | null>(null)
const provisioningState = reactive<{
  type: OnboardingType
  delivery: ProvisioningDelivery
  email: string
}>({
  type: 'SELF_SERVICE',
  delivery: 'SESSION',
  email: '',
})

// Only the unified self-service wizard can be opened in the caller's session.
const canOpenInSession = computed(() => provisioningState.type === 'SELF_SERVICE')
const usesSessionDelivery = computed(
  () => canOpenInSession.value && provisioningState.delivery === 'SESSION'
)

const selectedProvisioningDescription = computed(
  () => provisioningDescriptions[provisioningState.type] || ''
)

const openProvisioningModal = (customer: Customer) => {
  provisioningCustomer.value = customer
  provisioningState.type = 'SELF_SERVICE'
  provisioningState.delivery = 'SESSION'
  provisioningState.email = customer.brevoEmail || ''
  isProvisioningModalOpen.value = true
}

const sendProvisioningInvitation = async () => {
  const customer = provisioningCustomer.value
  if (!customer) return

  sendingProvisioning.value = true
  try {
    const { startCustomerProvisioning, startSelfService, startOIDCRecovery } = useOnboarding()
    const email = provisioningState.email.trim()

    // The unified wizard can be opened right away in this session.
    if (usesSessionDelivery.value) {
      await startSelfService(customer.customerNo)
      isProvisioningModalOpen.value = false
      window.location.assign('/onboarding/setup')
      return
    }

    // OIDC recovery has its own portal-admin endpoint which always mails a
    // fresh invitation; the other types use the customer provisioning endpoint
    // with email delivery so no bootstrap session is opened here.
    if (provisioningState.type === 'OIDC_RECOVERY') {
      await startOIDCRecovery(customer.customerNo, email)
    } else {
      await startCustomerProvisioning(customer.customerNo, provisioningState.type, undefined, {
        delivery: 'EMAIL',
        adminEmail: email,
      })
    }

    isProvisioningModalOpen.value = false
    toast.add({
      title: 'Einladung versendet',
      description: `Der Einladungslink wurde an ${email} gesendet.`,
      color: 'success',
    })
  } catch (err) {
    toast.add({
      title: 'Einladung konnte nicht versendet werden',
      description:
        (err as { message?: string })?.message || 'Ein unerwarteter Fehler ist aufgetreten',
      color: 'error',
    })
  } finally {
    sendingProvisioning.value = false
  }
}

const showBillingButton = computed(() => {
  const userInfo = authStore.userInfo
  if (!userInfo) return false

  return (
    isPortalAdmin(userInfo.user.roles) ||
    userInfo.user.groups?.includes('portal admin') ||
    userInfo.user.groups?.includes('mbcom-portal-admins')
  )
})

const isBillingModalOpen = ref(false)
const triggeringBilling = ref(false)
const isBillingDryRunOpen = ref(false)

const openBillingDryRun = () => {
  isBillingModalOpen.value = false
  isBillingDryRunOpen.value = true
}
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const sendingTest = ref(false)
const testingOIDC = ref(false)

const isModalOpen = ref(false)
// Onboarding invitation options for newly created customers: creating a customer
// hands the self-service wizard over to the customer by email instead of
// requiring a separate provisioning request.
const sendOnboardingInvitation = ref(false)
const onboardingAdminEmail = ref('')
const isDeleteModalOpen = ref(false)
const customerToDelete = ref<Customer | null>(null)

const columns = [
  { accessorKey: 'customerNo', header: 'Nr.' },
  { accessorKey: 'customerName', header: 'Name' },
  { accessorKey: 'customerShortName', header: 'Kurzname' },
  { id: 'loginMode', header: 'Anmeldung' },
  { id: 'microtechCertificate', header: 'microtech-Zertifikat' },
  { id: 'microtechConnection', header: 'microtech-Verbindung' },
  { id: 'actions', header: '' },
]

const state = reactive<Customer>({
  id: null,
  customerName: '',
  customerNo: '',
  customerShortName: '',
  brevoEmail: '',
  brevoActive: false,
  upstreamOIDC: {
    issuer: '',
    clientId: '',
    clientSecret: '',
  },
  recoveryContacts: [],
  loginMode: 'oidc',
})

// Local users can only be chosen when the registry offers them.
const localLoginEnabled = computed(() => !!authStore.userInfo?.environment?.localLoginEnabled)
const showLoginModeChoice = computed(() => !state.id && localLoginEnabled.value)
// A local customer has neither an OIDC provider nor a recovery channel.
const isLocalCustomer = computed(() => state.loginMode === 'local')
const loginModeOptions = [
  { label: 'Lokale Benutzer', value: 'local' },
  { label: 'Eigener OIDC-Provider', value: 'oidc' },
]

// The recovery channel of the edited customer. It is projected into the customer
// record, so the administration can show it without an onboarding session.
const recoveryContacts = computed(() => state.recoveryContacts ?? [])
const verifiedRecoveryContacts = computed(() => recoveryContacts.value.filter((c) => c.verified))

const regeneratingRecoveryCodes = ref(false)
// The freshly generated codes are shown exactly once and never persisted client
// side; only their hashes exist server-side.
const regeneratedRecoveryCodes = ref<RecoveryCodesResult | null>(null)

// regenerateRecoveryCodesForCustomer replaces the code set of the edited
// customer. The endpoint demands customer-admin scope plus a step-up grant, so
// the grant is obtained first and the call is retried in one go.
const regenerateRecoveryCodesForCustomer = async () => {
  const customerNo = state.customerNo
  if (!customerNo) return

  regeneratingRecoveryCodes.value = true
  regeneratedRecoveryCodes.value = null
  try {
    const { confirmStepUpAndRegenerateRecoveryCodes } = useOnboarding()
    regeneratedRecoveryCodes.value = await confirmStepUpAndRegenerateRecoveryCodes(customerNo)
    toast.add({
      title: 'Wiederherstellungscodes neu erstellt',
      description:
        'Die bisherigen Codes sind ungültig. Die neuen Codes werden nur jetzt angezeigt – bitte sofort sichern.',
      color: 'success',
    })
  } catch (err) {
    toast.add({
      title: 'Wiederherstellungscodes konnten nicht erstellt werden',
      description:
        (err as { message?: string })?.message ||
        'Bitte melden Sie sich erneut an und versuchen Sie es noch einmal.',
      color: 'error',
    })
  } finally {
    regeneratingRecoveryCodes.value = false
  }
}

// downloadRegeneratedRecoveryCodes offers the one-time codes as a text file.
const downloadRegeneratedRecoveryCodes = () => {
  const { downloadRecoveryCodes } = useOnboarding()
  downloadRecoveryCodes(regeneratedRecoveryCodes.value)
}

const fetchCustomers = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/registry/v1/customers')
    customers.value = response.data
  } catch (error) {
    console.error('Failed to fetch customers', error)
  } finally {
    loading.value = false
  }
}

// Certificate expiry and connection status of the customers' microtech servers.
// Loaded separately because the TLS probes may take a few seconds; the list renders without it.
const certificateSummaries = ref<Record<string, CertificateExpirySummary | null>>({})
const connectionSummaries = ref<Record<string, ServerConnectionSummary | null>>({})

const fetchCertificates = async () => {
  try {
    const response = await axios.get<{ customers: CustomerCertificateExpiry[] }>(
      '/api/microtech/v1/microtech-servers/certificates',
    )
    const certificates: Record<string, CertificateExpirySummary | null> = {}
    const connections: Record<string, ServerConnectionSummary | null> = {}
    for (const customer of response.data?.customers ?? []) {
      certificates[customer.customerNo] = summarizeCertificateExpiry(customer.servers)
      connections[customer.customerNo] = summarizeServerConnection(customer.servers)
    }
    certificateSummaries.value = certificates
    connectionSummaries.value = connections
  } catch (error) {
    console.error('Failed to fetch microtech certificates', error)
  }
}

onMounted(() => {
  fetchCustomers()
  fetchCertificates()
})

const openCreateModal = () => {
  state.id = null
  state.customerName = ''
  state.customerNo = ''
  state.customerShortName = ''
  state.brevoEmail = ''
  state.brevoActive = false
  state.upstreamOIDC = {
    issuer: '',
    clientId: '',
    clientSecret: '',
  }
  state.recoveryContacts = []
  state.loginMode = localLoginEnabled.value ? 'local' : 'oidc'
  regeneratedRecoveryCodes.value = null
  sendOnboardingInvitation.value = true
  onboardingAdminEmail.value = ''
  isModalOpen.value = true
}

const openEditModal = (customer: Customer) => {
  state.id = customer.customerNo
  state.customerName = customer.customerName
  state.customerNo = customer.customerNo
  state.customerShortName = customer.customerShortName
  state.brevoEmail = customer.brevoEmail || ''
  state.brevoActive = customer.brevoActive || false
  state.upstreamOIDC = customer.upstreamOIDC
    ? { ...customer.upstreamOIDC }
    : {
        issuer: '',
        clientId: '',
        clientSecret: '',
      }
  state.recoveryContacts = customer.recoveryContacts ? [...customer.recoveryContacts] : []
  state.loginMode = customer.loginMode === 'local' ? 'local' : 'oidc'
  regeneratedRecoveryCodes.value = null
  sendOnboardingInvitation.value = false
  onboardingAdminEmail.value = ''
  isModalOpen.value = true
}

// sendInitialOnboardingInvitation dispatches the INITIAL onboarding invitation for a
// freshly created customer. Failures are reported separately so the customer record
// itself is never reported as failed.
const sendInitialOnboardingInvitation = async (customerNo: string, email: string) => {
  try {
    const { startCustomerProvisioning } = useOnboarding()
    await startCustomerProvisioning(customerNo, 'INITIAL', undefined, {
      delivery: 'EMAIL',
      adminEmail: email,
    })
    toast.add({
      title: 'Onboarding-Einladung versendet',
      description: `Der Einladungslink wurde an ${email} gesendet.`,
      color: 'success',
    })
  } catch (err) {
    toast.add({
      title: 'Kunde angelegt, Einladung fehlgeschlagen',
      description:
        (err as { message?: string })?.message ||
        'Die Onboarding-Einladung konnte nicht versendet werden. Sie können sie über die Aktion «Provisionierung anfordern» erneut senden.',
      color: 'error',
    })
  }
}

const saveCustomer = async () => {
  const isCreate = !state.id
  const createsLocal = isCreate && state.loginMode === 'local'
  const invitationEmail = onboardingAdminEmail.value.trim()

  if (isCreate && !createsLocal && sendOnboardingInvitation.value && !invitationEmail) {
    toast.add({
      title: 'Validierungsfehler',
      description:
        'Bitte geben Sie die E-Mail-Adresse des Kunden-Administrators für die Onboarding-Einladung an.',
      color: 'warning',
    })
    return
  }

  if (state.brevoActive && !state.brevoEmail) {
    toast.add({
      title: 'Validierungsfehler',
      description: 'Bitte geben Sie eine Brevo E-Mail an, wenn Brevo aktiv ist.',
      color: 'warning',
    })
    return
  }

  saving.value = true
  try {
    const payload: Customer = {
      customerName: state.customerName,
      customerNo: state.customerNo,
      customerShortName: state.customerShortName,
      brevoEmail: state.brevoEmail,
      brevoActive: state.brevoActive,
    }

    if (isCreate) {
      payload.loginMode = createsLocal ? 'local' : 'oidc'
    }

    // Local customers have no upstream provider.
    if (!createsLocal) {
      if (state.upstreamOIDC?.issuer && state.upstreamOIDC?.clientId) {
        payload.upstreamOIDC = {
          issuer: state.upstreamOIDC.issuer,
          clientId: state.upstreamOIDC.clientId,
        }
        if (state.upstreamOIDC.clientSecret) {
          payload.upstreamOIDC.clientSecret = state.upstreamOIDC.clientSecret
        }
      } else {
        payload.upstreamOIDC = undefined
      }
    }

    if (state.id) {
      await axios.put(`/api/registry/v1/customers/${state.id}`, payload)
    } else {
      await axios.post('/api/registry/v1/customers', payload)
    }
    isModalOpen.value = false
    await fetchCustomers()

    if (isCreate && !createsLocal && sendOnboardingInvitation.value) {
      await sendInitialOnboardingInvitation(payload.customerNo, invitationEmail)
    }
  } catch (error) {
    console.error('Failed to save customer', error)
    let description = 'Fehler beim Speichern des Kunden.'
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409 && typeof error.response?.data?.code === 'string') {
        // A coded conflict (e.g. local login disabled) carries its own message.
        description = error.response.data.error || description
      } else if (error.response?.status === 409) {
        description = `Ein Kunde mit der Kundennummer "${state.customerNo}" existiert bereits.`
      } else if (typeof error.response?.data?.error === 'string') {
        description = error.response.data.error
      }
    }
    toast.add({
      title: 'Fehler',
      description,
      color: 'danger',
    })
  } finally {
    saving.value = false
  }
}

const sendTestEmail = async () => {
  const email = state.brevoEmail?.trim()
  if (!email) return

  sendingTest.value = true
  try {
    const payload = { email }
    if (state.id) {
      await axios.post(`/api/registry/v1/customers/${state.id}/test-email`, payload)
    } else {
      await axios.post('/api/registry/v1/customers/test-email', payload)
    }
    toast.add({
      title: 'Erfolg',
      description: 'Test E-Mail wurde erfolgreich versendet.',
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to send test email', error)
    let description = 'Fehler beim Versenden der Test E-Mail.'
    if (axios.isAxiosError(error) && typeof error.response?.data?.error === 'string') {
      description = error.response.data.error
    }
    toast.add({
      title: 'Fehler',
      description,
      color: 'danger',
    })
  } finally {
    sendingTest.value = false
  }
}

const testOIDC = async () => {
  if (!state.upstreamOIDC?.issuer) return

  testingOIDC.value = true
  try {
    const response = await axios.post('/api/registry/v1/customers/test-oidc', state.upstreamOIDC)
    if (response.data.ok) {
      toast.add({
        title: 'Erfolg',
        description: response.data.message,
        color: 'success',
      })
    } else {
      toast.add({
        title: 'Konfigurationsfehler',
        description: response.data.message,
        color: 'warning',
      })
    }
  } catch (error) {
    console.error('Failed to test OIDC', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Testen der OIDC Konfiguration.',
      color: 'danger',
    })
  } finally {
    testingOIDC.value = false
  }
}

const confirmDelete = (customer: Customer) => {
  customerToDelete.value = customer
  isDeleteModalOpen.value = true
}

const deleteCustomer = async () => {
  if (!customerToDelete.value) return
  deleting.value = true
  try {
    await axios.delete(`/api/registry/v1/customers/${customerToDelete.value.customerNo}`)
    isDeleteModalOpen.value = false
    await fetchCustomers()
  } catch (error) {
    console.error('Failed to delete customer', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Löschen des Kunden.',
      color: 'danger',
    })
  } finally {
    deleting.value = false
  }
}

const triggerBilling = async () => {
  triggeringBilling.value = true
  try {
    await axios.post('/api/billing/v1/test')
    isBillingModalOpen.value = false
    toast.add({
      title: 'Erfolg',
      description: 'Abrechnung wurde erfolgreich angestoßen.',
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to trigger billing', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Anstoßen der Abrechnung.',
      color: 'danger',
    })
  } finally {
    triggeringBilling.value = false
  }
}
</script>
